import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import * as saveAs from 'file-saver';
import { SnoozeIncidentDialogComponent } from 'src/app/common/modules/dialog-pop-ups/components/snooze-incident-dialog/snooze-incident-dialog.component';
import { VerifyIncidentDialogComponent } from 'src/app/common/modules/dialog-pop-ups/components/verify-incident-dialog/verify-incident-dialog.component';
import { ScreenType } from 'src/app/enums/ScreenType';
import { IIncidents } from 'src/app/models/incident';
import {
  EncryptionService,
  UserDetailsToken,
} from 'src/app/services/Encryption/encryption.service';
import { AumyeResponsiveScreenService } from 'src/app/services/screenResize/aumye-responsive-screen.service';

@Component({
  selector: 'app-incident-dashboard-table',
  templateUrl: './incident-dashboard-table.component.html',
  styleUrls: ['./incident-dashboard-table.component.scss'],
})
export class IncidentDashboardTableComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) dataSource: MatTableDataSource<IIncidents>;
  @Output() initCreateManualEvent = new EventEmitter<boolean>();
  @Output() verifyIncident = new EventEmitter<any>();
  @Output() showIncidentDetail = new EventEmitter<any>();
  @Output() refreshIncidentData = new EventEmitter<any>();
  @Output() snoozeIncident = new EventEmitter<any>();
  @Input() set incidentTableData(val: IIncidents[]) {
    this.temp = val;
    this.filtered = this.temp;
    this.filteredDataSource = new MatTableDataSource(this.temp);
    this.initializeFilterOptions();
    this.onUpdateExistingDataTable();
  }
  @Input() set isIncidentDetailsClosed(val: boolean) {
    if (!val) {
      this.isShowDetails = false;
      this.expandedDetail = '';
    }
  }
  filteredDataSource: MatTableDataSource<IIncidents>;
  rowIndex = 0;
  isShowDetails = false;
  expandedDetail = '';
  role: string = null;
  displayedColumns = [
    'name',
    'date',
    'time',
    'asset',
    'reporter',
    'action',
    'Details',
  ];
  temp: IIncidents[] = [];
  filtered: IIncidents[] = [];
  screenSize: ScreenType;

  // Enhanced Filter Properties
  selectedPrimaryFilter = 'all';
  selectedMyIssuesType = 'all';
  selectedAssignedUsers: string[] = [];
  selectedReporters: string[] = [];
  selectedStatus = '-1';

  // Available options for dropdowns
  availableAssignedUsers: { userId: string; userName: string }[] = [];
  availableReporters: string[] = [];

  constructor(
    private dialog: MatDialog,
    private screen: AumyeResponsiveScreenService,
    private encryptionService: EncryptionService
  ) {
    this.screen.getScreenType().subscribe((screenData) => {
      this.screenSize = screenData;
    });
  }
  userData: UserDetailsToken;

  ngOnInit(): void {
    this.userData = this.encryptionService.getCurrentUser();
    this.role = this.userData.role;
    this.filteredDataSource.paginator = this.paginator;
    this.filteredDataSource.sort = this.sort;
  }

  /**
   * Initialize filter options based on available data
   */
  initializeFilterOptions(): void {
    if (this.temp && this.temp.length > 0) {
      // Get unique assigned users (handling multiple assignees)
      const allAssignedUsers: { userId: string; userName: string }[] = [];

      this.temp.forEach((incident) => {
        try {
          // Parse the assignedUsers JSON string if it exists
          const assigneeList: any[] = JSON.parse(
            incident.assignedUsers || '[]'
          );
          if (assigneeList.length > 0) {
            assigneeList.forEach((x) => {
              if (x.UserId && x.UserName && x.UserName.trim() !== '') {
                allAssignedUsers.push({
                  userId: x.UserId,
                  userName: x.UserName,
                });
              }
            });
          }
          // Fallback to assignedTo if assignedUsers is empty
          else if (incident.assignedTo && incident.assignedTo.trim() !== '') {
            allAssignedUsers.push({
              userId: incident.assignedToUserId || incident.assignedTo,
              userName: incident.assignedTo,
            });
          }
        } catch (error) {
          // If JSON parsing fails, fallback to assignedTo
          if (incident.assignedTo && incident.assignedTo.trim() !== '') {
            allAssignedUsers.push({
              userId: incident.assignedToUserId || incident.assignedTo,
              userName: incident.assignedTo,
            });
          }
        }
      });

      // Remove duplicates based on userId and sort by userName
      const uniqueUsers = allAssignedUsers
        .filter(
          (user, index, self) =>
            index === self.findIndex((u) => u.userId === user.userId)
        )
        .sort((a, b) => a.userName.localeCompare(b.userName));

      this.availableAssignedUsers = uniqueUsers;

      // Get unique reporters
      this.availableReporters = [
        ...new Set(
          this.temp
            .map((incident) => incident.eventCreatedBy)
            .filter((reporter) => reporter && reporter.trim() !== '')
        ),
      ].sort();
    }
  }

  /**
   * Handle primary filter change
   */
  onPrimaryFilterChange(event: any): void {
    this.selectedPrimaryFilter = event.value;

    // Reset sub-filters when primary filter changes
    this.selectedMyIssuesType = 'all';
    this.selectedAssignedUsers = [];
    this.selectedReporters = [];

    this.applyAllFilters();
  }

  /**
   * Handle my issues type change
   */
  onMyIssuesTypeChange(event: any): void {
    this.selectedMyIssuesType = event.value;
    this.applyAllFilters();
  }

  /**
   * Handle assigned users change
   */
  onAssignedUsersChange(event: any): void {
    this.selectedAssignedUsers = event.value;
    this.applyAllFilters();
  }

  /**
   * Handle reporters change
   */
  onReportersChange(event: any): void {
    this.selectedReporters = event.value;
    this.applyAllFilters();
  }

  /**
   * Apply all filters based on current selections
   */
  applyAllFilters(): void {
    let filteredData = [...this.temp];

    // Apply primary filter
    switch (this.selectedPrimaryFilter) {
      case 'my-issues':
        filteredData = this.applyMyIssuesFilter(filteredData);
        break;
      case 'assigned-to':
        filteredData = this.applyAssignedToFilter(filteredData);
        break;
      case 'reported-by':
        filteredData = this.applyReportedByFilter(filteredData);
        break;
      case 'all':
      default:
        // No additional filtering needed for 'all'
        break;
    }

    // Apply status filter
    const selectedStatus = +this.selectedStatus;
    if (selectedStatus !== -1) {
      filteredData = filteredData.filter(
        (incident: IIncidents) => incident.incidentStatus === selectedStatus
      );
    }

    this.filteredDataSource.data = filteredData;

    // Reset pagination
    if (this.filteredDataSource.paginator) {
      this.filteredDataSource.paginator.firstPage();
    }
  }

  /**
   * Apply my issues filter
   */
  private applyMyIssuesFilter(data: IIncidents[]): IIncidents[] {
    const currentUser = this.userData;
    const currentUserId = currentUser.userID;
    const currentUserName = currentUser.fullName || currentUser.firstName;

    switch (this.selectedMyIssuesType) {
      case 'reported':
        return data.filter(
          (incident) =>
            incident.eventCreatedByUserID === currentUserId ||
            incident.eventCreatedBy === currentUserName
        );
      case 'assigned':
        return data.filter((incident) => {
          // Check if user is in the assignedUsers list
          try {
            const assigneeList: any[] = JSON.parse(
              incident.assignedUsers || '[]'
            );
            const isAssigned = assigneeList.some(
              (x) => x.UserId === currentUserId
            );

            // Also check the assignedToUserId field as fallback
            const isAssignedTo =
              incident.assignedToUserId === currentUserId ||
              incident.assignedTo === currentUserName;

            return isAssigned || isAssignedTo;
          } catch (error) {
            // Fallback to assignedTo if JSON parsing fails
            return (
              incident.assignedToUserId === currentUserId ||
              incident.assignedTo === currentUserName
            );
          }
        });
      case 'all':
      default:
        return data.filter((incident) => {
          const isReporter =
            incident.eventCreatedByUserID === currentUserId ||
            incident.eventCreatedBy === currentUserName;

          // Check if user is assigned
          try {
            const assigneeList: any[] = JSON.parse(
              incident.assignedUsers || '[]'
            );
            const isAssigned = assigneeList.some(
              (x) => x.UserId === currentUserId
            );
            const isAssignedTo =
              incident.assignedToUserId === currentUserId ||
              incident.assignedTo === currentUserName;

            return isReporter || isAssigned || isAssignedTo;
          } catch (error) {
            return (
              isReporter ||
              incident.assignedToUserId === currentUserId ||
              incident.assignedTo === currentUserName
            );
          }
        });
    }
  }

  /**
   * Apply assigned to filter
   */
  private applyAssignedToFilter(data: IIncidents[]): IIncidents[] {
    if (this.selectedAssignedUsers.length === 0) {
      return data;
    }

    return data.filter((incident) => {
      try {
        // Parse the assignedUsers JSON string if it exists
        const assigneeList: any[] = JSON.parse(incident.assignedUsers || '[]');

        // Check if any of the selected user IDs is in the assignee list
        const hasSelectedAssignee = assigneeList.some((assignee) =>
          this.selectedAssignedUsers.includes(assignee.UserId)
        );

        // Also check the assignedToUserId field as fallback
        const isAssignedTo = this.selectedAssignedUsers.includes(
          incident.assignedToUserId
        );

        return hasSelectedAssignee || isAssignedTo;
      } catch (error) {
        // Fallback to assignedToUserId if JSON parsing fails
        return this.selectedAssignedUsers.includes(incident.assignedToUserId);
      }
    });
  }

  /**
   * Apply reported by filter
   */
  private applyReportedByFilter(data: IIncidents[]): IIncidents[] {
    if (this.selectedReporters.length === 0) {
      return data;
    }

    return data.filter((incident) =>
      this.selectedReporters.includes(incident.eventCreatedBy)
    );
  }

  /**
   * Clear all filters and reset to default state
   */
  clearAllFilters(): void {
    this.selectedPrimaryFilter = 'all';
    this.selectedMyIssuesType = 'all';
    this.selectedAssignedUsers = [];
    this.selectedReporters = [];
    this.selectedStatus = '-1';

    // Clear search input
    const searchInput = document.querySelector(
      'input[matInput]'
    ) as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }

    // Reset data source
    this.filteredDataSource.data = this.temp;
    this.filteredDataSource.filter = '';

    if (this.filteredDataSource.paginator) {
      this.filteredDataSource.paginator.firstPage();
    }
  }

  applyFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filteredDataSource.filter = filterValue.trim().toLowerCase();
    if (this.filteredDataSource.paginator) {
      this.filteredDataSource.paginator.firstPage();
    }
    // Filter the temp array
    this.filtered = this.temp.filter((incident) => {
      return incident.incidentName.trim().toLowerCase().includes(filterValue);
    });
  }

  applyFilterCustom(event: any) {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    // Start with the currently filtered data based on other filters
    let baseData = [...this.temp];

    // Apply primary filter first
    switch (this.selectedPrimaryFilter) {
      case 'my-issues':
        baseData = this.applyMyIssuesFilter(baseData);
        break;
      case 'assigned-to':
        baseData = this.applyAssignedToFilter(baseData);
        break;
      case 'reported-by':
        baseData = this.applyReportedByFilter(baseData);
        break;
    }

    // Apply status filter
    const selectedStatus = +this.selectedStatus;
    if (selectedStatus !== -1) {
      baseData = baseData.filter(
        (incident: IIncidents) => incident.incidentStatus === selectedStatus
      );
    }

    // Apply text search filter
    const filtered = baseData.filter((incident: IIncidents) => {
      const incidentNameMatch = incident.incidentName
        ?.toLowerCase()
        .includes(filterValue);
      const incidentIdMatch = incident.incidentId
        ?.toString()
        .toLowerCase()
        .includes(filterValue);
      const incidentStatusMatch = incident.incidentStatus
        ?.toString()
        .toLowerCase()
        .includes(filterValue);
      const assigneeMatch = incident.assignedTo
        ?.toString()
        .toLowerCase()
        .includes(filterValue);
      const reporterMatch = incident.eventCreatedBy
        ?.toString()
        .toLowerCase()
        .includes(filterValue);

      return (
        incidentIdMatch ||
        incidentStatusMatch ||
        incidentNameMatch ||
        reporterMatch ||
        assigneeMatch
      );
    });

    // Update the MatTableDataSource with the filtered array
    this.filteredDataSource.data = filtered;

    // Go back to first page if paginator exists
    if (this.filteredDataSource.paginator) {
      this.filteredDataSource.paginator.firstPage();
    }
  }

  onChangeDataTable(event: any) {
    this.selectedStatus = event.value;
    this.applyAllFilters();
  }

  onUpdateExistingDataTable() {
    this.applyAllFilters();
  }

  onShowDetails(val: any) {
    this.isShowDetails = true;
    this.expandedDetail = val.incidentId;
    this.showIncidentDetail.emit(val);
  }
  onCreateManualEvent() {
    this.initCreateManualEvent.emit();
  }
  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.displayedColumns,
      event.previousIndex,
      event.currentIndex
    );
  }
  downloadCSV() {
    const data: any = this.temp;
    const replacer = (key: any, value: any) => (value === null ? '' : value); // specify how you want to handle null values here
    const header = Object.keys(data[0]);
    let csv = data.map((row: any) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    csv.unshift(header.join(','));
    let csvArray = csv.join('\r\n');
    var blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, 'myFile.csv');
  }
  onSnoozeIncident(val: IIncidents) {
    const dialogRef = this.dialog.open(SnoozeIncidentDialogComponent, {
      disableClose: true,
      data: val,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      let data = {
        isSnooze: true,
        durationType: result.durationType,
        durationSize: result.durationSize,
        incidentId: val.incidentId,
        orgId: val.orgId,
        incidenType: val.incidentType,
        incidentName: val.incidentName,
        incidentStatusTimeline: val.incidentStatusTimeline,
        assignedBy: this.userData.fullName,
        assignedById: this.userData.userID,
        createBy: val.eventCreatedBy,
        createById: val.eventCreatedByUserID,
        assignedTo: val.assignedTo,
        assignedToUserId: val.assignedToUserId,
        evidence: val.eventEvidence,
      };
      if (result) {
        this.snoozeIncident.emit(data);
      }
    });
  }
  onRefreshIncidet() {
    this.refreshIncidentData.emit();
  }
  getFormatedDate(val: any) {
    const date = new Date(val + 'Z'); // Convert UTC to local tim
    return date;
  }
  onClickStatus(val: any) {
    const dialogRef = this.dialog.open(VerifyIncidentDialogComponent, {
      disableClose: true,
      data: val,
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      let data = {
        status:
          result == 'Open'
            ? '0'
            : result == 'Close'
            ? '1'
            : result == 'Verified'
            ? '2'
            : result == 'Snooze'
            ? '3'
            : result == 'In-Progress'
            ? '4'
            : result == 'On-Hold'
            ? '5'
            : result == 'Approved'
            ? '6'
            : result == 'Rejected'
            ? '7'
            : result == 'Completed'
            ? '8'
            : '',
        incidentId: val.incidentId,
      };
      if (data.status == '3') {
        const dialogRef = this.dialog.open(SnoozeIncidentDialogComponent, {
          disableClose: true,
          data: val,
        });
        dialogRef.afterClosed().subscribe((result: any) => {
          let data = {
            isSnooze: true,
            durationType: result.durationType,
            durationSize: result.durationSize,
            incidentId: val.incidentId,
            status: '3',
          };
          if (result) {
            this.verifyIncident.emit(data);
          }
        });
        // this.onSnoozeIncident(val);
      } else {
        if (result) {
          this.verifyIncident.emit(data);
        }
      }
    });
  }

  ScreenType = ScreenType;
}
